import { CSSResultGroup, html, LitElement, nothing, type PropertyValues, TemplateResult } from 'lit'
import { customElement, property, queryAll, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import type { CSECheckbox, CSEPasswordInput, CSETextInput } from '@cloudsuite/mosaic-elements'
import { style } from './login.style.js'
import { loginStep1, loginStep2, resendMfaCode } from './login-api.js'
import {
  LoginPasswordExpiredEvent,
  LoginResendEvent,
  LoginSubmitEvent,
  LoginSuccessEvent,
  LoginVerifyEvent,
} from './login-events.js'

type Step = 'login' | 'verify'
type Variant = 'email' | 'mfa'
type DisplayMode = 'show' | 'hide'

export const headingVariants = [
  'body',
  'body-s',
  'heading-1',
  'heading-2',
  'heading-3',
  'heading-4',
  'heading-5',
  'heading-6',
  'caption-l',
  'caption-m',
  'caption-s',
] as const
type HeadingVariantsType = (typeof headingVariants)[number]

export const loginButtonVariants = [
  'default',
  'primary',
  'secondary',
  'error',
  'info',
  'success',
  'warning',
] as const
export type LoginButtonVariantsType = (typeof loginButtonVariants)[number]

initializeIconsByRole({
  'cse-login.check': { name: 'circle-check', style: 'regular' },
  'cse-login.back': { name: 'angle-left', style: 'light' },
})

@customElement('csc-login')
export class CSCLogin extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String, reflect: true })
  variant: Variant = 'mfa'

  @property({ type: String, attribute: 'api-base-url' })
  apiBaseUrl = ''

  /** Post-login redirect; also sent as `return_url` on step1 when `returnUrl` is empty. */
  @property({ type: String, attribute: 'redirect-url' })
  redirectUrl = ''

  /** Passed through to the login API as `return_url` (preferred over `redirect-url`). */
  @property({ type: String, attribute: 'return-url' })
  returnUrl = ''

  @property({ type: Boolean, attribute: 'disable-redirect' })
  disableRedirect = false

  @property({ type: String, attribute: 'forgot-password-url' })
  forgotPasswordUrl = ''

  /** Login step only. */
  @property({ type: String, attribute: 'title-display' })
  titleDisplay: DisplayMode = 'show'

  /** Login step only. */
  @property({ type: String, attribute: 'description-display' })
  descriptionDisplay: DisplayMode = 'show'

  /** Applied to slotted `cse-text[slot="heading"]` on the login step. */
  @property({ type: String, attribute: 'heading-variant' })
  headingVariant: HeadingVariantsType = 'heading-2'

  /** Applied to slotted `cse-text[slot="description"]` on the login step. */
  @property({ type: String, attribute: 'description-variant' })
  descriptionVariant: HeadingVariantsType = 'body'

  /** Login submit button only (verify step stays primary). */
  @property({ type: String, attribute: 'login-button-variant' })
  loginButtonVariant: LoginButtonVariantsType = 'primary'

  @state()
  private step: Step = 'login'

  @state()
  private maskedEmail = ''

  @state()
  private codes: string[] = ['', '', '', '', '', '']

  @state()
  private resendSuccess = false

  @state()
  private loginErrorMessage = ''

  @state()
  private verifyErrorMessage = ''

  @state()
  private mfaToken: string | null = null

  @state()
  private resendCountdownSeconds = 0

  @state()
  private isLoading = false

  private resendCooldownInterval: number | null = null

  /** Step1 `return_url` when MFA is required; used after step2 if that response omits it. */
  private mfaPendingReturnUrl: string | null = null

  @queryAll('.code-input cse-text-input')
  private codeInputs!: NodeListOf<CSETextInput>

  override connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener('keydown', this.handleHostKeydown, { capture: true })
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListener('keydown', this.handleHostKeydown, { capture: true })
    this.clearResendCooldown()
  }

  protected override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)
    if (this.step === 'verify' && (changedProperties.has('codes') || changedProperties.has('step'))) {
      this.syncCodeInputValues()
    }
    if (changedProperties.has('step') && this.step === 'verify') {
      this.startResendCooldown(60)
    }
    if (
      this.step === 'login' &&
      (changedProperties.has('headingVariant') ||
        changedProperties.has('descriptionVariant') ||
        changedProperties.has('titleDisplay') ||
        changedProperties.has('descriptionDisplay') ||
        changedProperties.has('step'))
    ) {
      this.syncLoginStepSlottedTextVariants()
    }
  }

  private syncLoginStepSlottedTextVariants() {
    this.applyTextVariantToSlot('heading', this.headingVariant)
    this.applyTextVariantToSlot('description', this.descriptionVariant)
  }

  private applyTextVariantToSlot(slotName: string, variant: HeadingVariantsType) {
    const slot = this.shadowRoot?.querySelector(`slot[name="${slotName}"]`)
    if (!(slot instanceof HTMLSlotElement)) return
    slot.assignedElements({ flatten: true }).forEach((el: Element) => {
      if (el.tagName === 'CSE-TEXT') {
        ;(el as HTMLElement & { variant: string }).variant = variant
      }
    })
  }

  /** cse-text-input keeps internal state; push digits to host + native input after every codes change. */
  private syncCodeInputValues() {
    this.codeInputs?.forEach((el, i) => {
      const digit = this.codes[i] ?? ''
      if (el.value !== digit) {
        el.value = digit
      }
      const native = el.shadowRoot?.querySelector('input')
      if (native instanceof HTMLInputElement && native.value !== digit) {
        native.value = digit
      }
    })
  }

  private getRedirectUrl(): string {
    return this.returnUrl || this.redirectUrl || '/'
  }

  private getReturnUrlForApi(): string {
    return this.returnUrl || this.redirectUrl || ''
  }

  private pickReturnUrlFromPayload(data: unknown): string | null {
    if (!data || typeof data !== 'object') return null
    const record = data as Record<string, unknown>
    for (const key of ['return_url', 'redirect_url']) {
      const raw = record[key]
      if (typeof raw !== 'string') continue
      const trimmed = raw.trim()
      if (trimmed.length > 0) return trimmed
    }
    return null
  }

  private hasMfaToken(data: unknown): boolean {
    if (!data || typeof data !== 'object') return false
    const token = (data as { mfa_token?: unknown }).mfa_token
    return token != null && String(token).trim().length > 0
  }

  /** API signalled failure; `authenticated: true` overrides an explicit `success: false`. */
  private isExplicitLoginFailure(data: unknown): boolean {
    if (!data || typeof data !== 'object') return false
    const record = data as Record<string, unknown>
    if (record['authenticated'] === true) return false
    return 'success' in record && record['success'] === false
  }

  /** API requires a password change before login; `return_url` points to the change password page. */
  private pickPasswordExpiredUrl(data: unknown): string | null {
    if (!data || typeof data !== 'object') return null
    if ((data as Record<string, unknown>)['password_expired'] !== true) return null
    return this.pickReturnUrlFromPayload(data)
  }

  private handlePasswordExpired(data: unknown, returnUrl: string): void {
    this.clearLoginError()
    this.clearVerifyError()
    this.dispatchEvent(new LoginPasswordExpiredEvent({ returnUrl, data }))
    this.redirectAfterSuccess(returnUrl)
  }

  private completeLoginAfterSuccess(
    data: unknown,
    context: { email?: string; rememberMe?: boolean; returnUrlFallback?: string | null } = {},
  ): void {
    this.clearLoginError()
    this.clearVerifyError()
    const detail: { email?: string; rememberMe?: boolean; data: unknown } = { data }
    if (context.email !== undefined) detail.email = context.email
    if (context.rememberMe !== undefined) detail.rememberMe = context.rememberMe
    this.dispatchEvent(new LoginSuccessEvent(detail))
    this.redirectAfterSuccess(
      this.pickReturnUrlFromPayload(data) ?? context.returnUrlFallback ?? this.getRedirectUrl(),
    )
  }

  private pickErrorMessageFromPayload(data: unknown): string | null {
    if (!data || typeof data !== 'object') return null
    const record = data as Record<string, unknown>
    for (const key of ['message', 'error_message', 'error', 'detail']) {
      const value = record[key]
      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (trimmed.length > 0) return trimmed
      }
    }
    return null
  }

  private clearLoginError() {
    this.loginErrorMessage = ''
  }

  private clearVerifyError() {
    this.verifyErrorMessage = ''
  }

  private redirectAfterSuccess(targetUrl?: string | null) {
    if (this.disableRedirect) return
    const trimmed = targetUrl?.trim() ?? ''
    const target = trimmed.length > 0 ? trimmed : this.getRedirectUrl()
    // Bracket access: keeps a single runtime dispatch so tests can shadow `assignLocation` on the instance.
    ;(this as CSCLogin & { assignLocation(url: string): void })['assignLocation'](target)
  }

  /**
   * Performs post-auth navigation. Tests may replace `assignLocation` on the **instance**
   * (dist builds can diverge from `CSCLogin.prototype` used in the test module).
   */
  assignLocation(url: string): void {
    try {
      window.location.assign(url)
    } catch {
      // intentionally empty — window.location.assign throws in test environments
    }
  }

  private get allCodesFilled(): boolean {
    return this.codes.every(code => code.length === 1)
  }

  private get formattedResendCountdown(): string {
    const minutes = Math.floor(this.resendCountdownSeconds / 60)
    const seconds = this.resendCountdownSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  private clearResendCooldown() {
    if (this.resendCooldownInterval !== null) {
      window.clearInterval(this.resendCooldownInterval)
      this.resendCooldownInterval = null
    }
  }

  private startResendCooldown(seconds = 60) {
    this.clearResendCooldown()
    this.resendCountdownSeconds = seconds

    this.resendCooldownInterval = window.setInterval(() => {
      if (this.resendCountdownSeconds <= 1) {
        this.resendCountdownSeconds = 0
        this.clearResendCooldown()
        return
      }
      this.resendCountdownSeconds -= 1
    }, 1000)
  }

  private maskEmail(email: string): string {
    const [local = '', domain = ''] = email.split('@')
    const maskedLocal = local.slice(0, 3) + '*'.repeat(Math.max(local.length - 3, 2))
    const domainParts = domain.split('.').filter(Boolean)
    const domainRoot = domainParts[0] ?? ''
    const maskedDomain = domainRoot.slice(0, 3) + '*'.repeat(Math.max(domainRoot.length - 3, 4))
    const tld = domainParts.slice(1).join('.')
    return `${maskedLocal}@${maskedDomain}${tld ? `.${tld}` : ''}`
  }

  private submitActiveForm() {
    const form = this.shadowRoot?.querySelector('form')
    if (!form) return
    form.dispatchEvent(new SubmitEvent('submit', { bubbles: true, cancelable: true }))
  }

  /**
   * Enter in native inputs inside custom elements does not trigger implicit form submit.
   * Submit explicitly from the host (login) and code fields (verify).
   */
  private handleHostKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter') return

    const form = this.shadowRoot?.querySelector('form')
    if (!form || !event.composedPath().includes(form)) return

    const origin = event.composedPath()[0]
    if (!(origin instanceof Element)) return
    if (origin.closest('cse-link, cse-checkbox')) return
    if (origin.closest('cse-button')) return
    if (origin.closest('.code-input')) return

    if (this.step === 'verify' && (!this.allCodesFilled || this.isLoading)) return
    if (this.step === 'login' && this.isLoading) return

    event.preventDefault()
    this.submitActiveForm()
  }

  private handleLogin = async (event: SubmitEvent) => {
    event.preventDefault()

    if (this.isLoading) return

    const form = event.currentTarget as HTMLFormElement | null
    if (!form) return

    this.mfaPendingReturnUrl = null

    // Read from custom elements: FormData often misses cse-password-input / cse-checkbox values
    const emailEl = form.querySelector('cse-text-input[type="email"]') as CSETextInput | null
    const passwordEl = form.querySelector('cse-password-input') as CSEPasswordInput | null
    const rememberEl = form.querySelector('cse-checkbox') as CSECheckbox | null
    const email = emailEl?.value ?? ''
    const password = passwordEl?.value ?? ''
    const rememberMe = rememberEl?.checked ?? false

    this.isLoading = true

    try {
      const response = await loginStep1(
        {
          email,
          password,
          remember: rememberMe,
          return_url: this.getReturnUrlForApi(),
        },
        { apiBaseUrl: this.apiBaseUrl },
      )
      const data = response.data
      const mfaToken = this.hasMfaToken(data) ? String((data as { mfa_token?: unknown }).mfa_token) : null
      const passwordExpiredUrl = response.ok ? this.pickPasswordExpiredUrl(data) : null

      if (passwordExpiredUrl) {
        this.handlePasswordExpired(data, passwordExpiredUrl)
        return
      } else if (!response.ok || this.isExplicitLoginFailure(data)) {
        this.loginErrorMessage = this.pickErrorMessageFromPayload(data) ?? ''
      } else if (this.variant === 'mfa' && mfaToken) {
        this.mfaPendingReturnUrl = this.pickReturnUrlFromPayload(data)
        this.mfaToken = mfaToken
        this.clearLoginError()
        this.clearVerifyError()
        this.maskedEmail = this.maskEmail(email)
        this.codes = ['', '', '', '', '', '']
        this.resendSuccess = false
        this.step = 'verify'
        return
      } else {
        // Email variant, or MFA not required — auth finished on step1.
        this.completeLoginAfterSuccess(data, { email, rememberMe })
        return
      }
    } catch (error) {
      console.error('[csc-login] MFA step1 request failed', error)
    } finally {
      this.isLoading = false
    }

    this.dispatchEvent(new LoginSubmitEvent({ email, password, rememberMe }))
  }

  private handleCodeInput(index: number, event: Event) {
    this.clearVerifyError()
    const input = event.target as CSETextInput
    const value = input.value.replace(/\D/g, '').slice(0, 1)
    const updatedCodes = [...this.codes]
    updatedCodes[index] = value
    this.codes = updatedCodes

    if (value && index < 5) {
      const nextInput = this.codeInputs[index + 1]
      nextInput?.focus()
    }

    if (this.allCodesFilled) {
      this.submitActiveForm()
    }
  }

  private handleCodeKeydown(index: number, event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      if (this.allCodesFilled) {
        this.submitActiveForm()
      }
      return
    }

    if (event.key !== 'Backspace') return

    event.preventDefault()
    const updatedCodes = [...this.codes]
    let focusIndex = index

    if (updatedCodes[index]) {
      updatedCodes[index] = ''
    } else if (index > 0) {
      updatedCodes[index - 1] = ''
      focusIndex = index - 1
    } else {
      return
    }

    this.codes = updatedCodes
    this.syncCodeInputValues()
    this.codeInputs[focusIndex]?.focus()
  }

  private handleCodePaste(event: ClipboardEvent) {
    const text = event.clipboardData?.getData('text') ?? ''
    const digits = text.replace(/\D/g, '')
    if (!digits) return

    event.preventDefault()
    this.clearVerifyError()

    const nextCodes = [...this.codes]
    for (let i = 0; i < 6; i++) {
      nextCodes[i] = digits.charAt(i) || nextCodes[i] || ''
    }
    this.codes = nextCodes
    this.syncCodeInputValues()

    const focusIndex = Math.min(digits.length, 6) - 1
    this.codeInputs[Math.max(0, focusIndex)]?.focus()

    if (this.allCodesFilled) {
      this.submitActiveForm()
    }
  }

  private handleVerify = async (event: SubmitEvent) => {
    event.preventDefault()
    if (!this.allCodesFilled || !this.mfaToken || this.isLoading) return

    const mfaCode = this.codes.join('')

    this.isLoading = true

    try {
      const response = await loginStep2({ mfaToken: this.mfaToken, mfaCode }, { apiBaseUrl: this.apiBaseUrl })
      const data = response.data
      const passwordExpiredUrl = response.ok ? this.pickPasswordExpiredUrl(data) : null

      if (passwordExpiredUrl) {
        this.handlePasswordExpired(data, passwordExpiredUrl)
        return
      } else if (!response.ok || this.isExplicitLoginFailure(data)) {
        this.verifyErrorMessage = this.pickErrorMessageFromPayload(data) ?? ''
      } else {
        this.completeLoginAfterSuccess(data, { returnUrlFallback: this.mfaPendingReturnUrl })
        return
      }
    } catch (error) {
      console.error('[csc-login] MFA step2 request failed', error)
    } finally {
      this.isLoading = false
    }

    await this.updateComplete
    this.codeInputs[this.codes.length - 1]?.focus()

    this.dispatchEvent(new LoginVerifyEvent({ code: mfaCode }))
  }

  private handleBackToLogin = (event: Event) => {
    event.preventDefault()
    this.step = 'login'
    this.mfaToken = null
    this.mfaPendingReturnUrl = null
    this.isLoading = false
    this.clearVerifyError()
    this.codes = ['', '', '', '', '', '']
    this.resendSuccess = false
    this.clearResendCooldown()
    this.resendCountdownSeconds = 0
  }

  private handleResend = async () => {
    this.codes = ['', '', '', '', '', '']
    this.resendSuccess = false
    this.clearVerifyError()
    this.codeInputs[0]?.focus()

    if (this.resendCountdownSeconds > 0) {
      return
    }

    if (!this.mfaToken) {
      console.error('[csc-login] Cannot resend MFA code: missing mfaToken')
      return
    }

    try {
      const response = await resendMfaCode({ mfaToken: this.mfaToken }, { apiBaseUrl: this.apiBaseUrl })

      // For now, treat any 2xx as success and show the success message
      if (response.ok) {
        this.resendSuccess = true
        this.startResendCooldown(60)
      }
    } catch (error) {
      console.error('[csc-login] MFA resend request failed', error)
    }

    this.dispatchEvent(new LoginResendEvent())
  }

  private renderLoginStep(): TemplateResult {
    return html`
      <form @submit="${this.handleLogin}">
        <div class="login-step-container">
          <div class="login-step-text-container">

          ${this.titleDisplay === 'show'
            ? html`<slot name="heading" @slotchange=${this.syncLoginStepSlottedTextVariants}></slot>`
            : nothing}
          ${this.descriptionDisplay === 'show'
            ? html`<slot name="description" @slotchange=${this.syncLoginStepSlottedTextVariants}></slot>`
            : nothing}
          </div>

          <cse-fieldset >
          ${this.loginErrorMessage
            ? html`
              <cse-alert variant="error">
                <cse-text slot="description">${this.loginErrorMessage}</cse-text>
              </cse-alert>
            ` : nothing}
          <cse-form-field>
            <cse-label slot="label">${t('email_label', { ns: 'csc-login', default: 'Email' })}</cse-label>
            <cse-text-input type="email" name="email" required ?disabled="${this.isLoading}"></cse-text-input>
          </cse-form-field>

          <cse-form-field>
            <cse-label slot="label">${t('password_label', { ns: 'csc-login', default: 'Password' })}</cse-label>
            <cse-password-input name="password" ?disabled="${this.isLoading}"></cse-password-input>
          </cse-form-field>

          <cse-checkbox name="remember">
            ${t('remember_me_label', { ns: 'csc-login', default: 'Remember me' })}
          </cse-checkbox>

          <cse-button type="submit" variant=${this.loginButtonVariant} fit="fill" ?disabled="${this.isLoading}">
            ${this.isLoading ? html`
              <cse-loader slot="prefix" animate></cse-loader>
            ` : html`
              ${t('login_button', { ns: 'csc-login', default: 'Login' })}
            `}
          </cse-button>

          ${this.forgotPasswordUrl
            ? html`
              <div class="csc-login__forgot">
                <cse-link url=${this.forgotPasswordUrl}>
                  ${t('forgot_password_link', { ns: 'csc-login', default: 'Forgot password?' })}
                </cse-link>
              </div>
            ` : nothing}
        </cse-fieldset>
        </div>
      </form>
    `
  }

  private renderVerifyStep(): TemplateResult {
    return html`
      <form @submit="${this.handleVerify}">
        <div class="verify-container">
          <div class="verify-step-text-container">
          <cse-text as="h2" variant="heading-2">
            ${t('verify_title', { ns: 'csc-login', default: 'Check your email' })}
          </cse-text>
          <cse-text as="p">
            ${t('verify_inbox', {
              ns: 'csc-login', 
              variable_email: this.maskedEmail,
              default: 'We sent a verification code to {{variable_email}}. If it doesn\'t arrive in a few minutes, check your spam folder or try requesting a new code.'
            })}
          </cse-text>
          </div>
          <cse-fieldset class="verify-step-form-container">
          <div class="code-input">
            ${this.codes.map(
              (_digit, index) => html`
                <cse-form-field>
                  <cse-text-input
                    name="code-${index}"
                    maxlength="1"
                    required
                    ?disabled="${this.isLoading}"
                    @input="${(e: Event) => this.handleCodeInput(index, e)}"
                    @keydown="${(e: KeyboardEvent) => this.handleCodeKeydown(index, e)}"
                    @paste="${(e: ClipboardEvent) => this.handleCodePaste(e)}"
                  ></cse-text-input>
                </cse-form-field>
              `
            )}
          </div>
          ${this.verifyErrorMessage
            ? html`
              <cse-text variant="caption-m" class="csc-login__verify-error">
                ${this.verifyErrorMessage}
              </cse-text>
            ` : nothing}
          <div class="csc-login__resend-wrap">
            ${this.resendCountdownSeconds > 0
              ? html`
                <cse-button disabled fit="fill" class="csc-login__resend-disabled">
                  ${t('resend_code_with_counter', { 
                    ns: 'csc-login', 
                    variable_time: this.formattedResendCountdown || 0, 
                    default: 'Request new code in {{variable_time}}'
                  })}
                </cse-button>
              `
              : html`
                <cse-button @click="${this.handleResend}" fit="fill">
                  ${t('resend_code', { ns: 'csc-login', default: 'Request new code' })}
                </cse-button>
              `}
          </div>
            ${this.resendSuccess
              ? html`
                <cse-text
                  variant="body-s"
                  class="csc-login__resend-success"
                  has-icon
                >
                  <cse-icon slot="prefix" icon-role="cse-login.check"></cse-icon>
                  ${t('resend_success', { ns: 'csc-login', default: 'Verification code sent. Please check your email.' })}
                </cse-text>
              `
            : ''}
        </cse-fieldset>
          <cse-link
            class="csc-login__back"
            variant="secondary"
            @click="${this.handleBackToLogin}"
          >
            <cse-icon slot="prefix" icon-role="cse-login.back"></cse-icon>
            ${t('back_to_login', { ns: 'csc-login', default: 'Back to login' })}
          </cse-link>
        </div>
      </form>
    `
  }

  protected override render(): TemplateResult {
    if (this.variant === 'email') return this.renderLoginStep()
    return this.step === 'login' ? this.renderLoginStep() : this.renderVerifyStep()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-login': CSCLogin
  }
}
